import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
  OnDestroy,
  computed,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WorkflowService } from '../../workflow.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  assigneeSelectDependencyEmpty,
  requestDetailsEmptyData,
} from '../../workflow.constants';
import {
  StepAction,
  AssigneeInputDependency,
  WorkFlowStep,
} from '../../workflow.model';
import { WorkflowStepInfoViewComponent } from './workflow-step-info-view/workflow-step-info-view.component';
import { WorkflowStepInfoFormComponent } from './workflow-step-info-form/workflow-step-info-form.component';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormArray,
} from '@angular/forms';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { EscalationTypeEnum } from './sla-type.enum';
import { LoaderComponent } from '../../../../../../ui-components/lib/loader/loader.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { RequestDetailsHeaderComponent } from '../../../../../common/components/request-details-header/request-details-header.component';
import { RequestActionEnum } from '../../../../../common/enums/request-action.enum';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { CanDeactivateComponent } from '../../../../../common/guards/can-deactivate/can-deactivate.guard';
import { baseUrl } from '../../../../../common/constants/endpoints.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { RequestDetailsResponse } from '../../../../../common/models/request-details.model';
import { AssigneeData } from '../../../../../common/models/assignee.model';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import {
  createStepActionFormGroupFromConfig,
  createStepActionsFormArray,
} from './workflow-step-info-form/workflow-configuration-step-action/workflow-configuration-step-action.const';

@Component({
  selector: 'lib-workflow-configuration-details',
  imports: [
    RequestDetailsHeaderComponent,
    TranslocoPipe,
    WorkflowStepInfoViewComponent,
    WorkflowStepInfoFormComponent,
    ReactiveFormsModule,
    OverlayLoaderComponent,
    LoaderComponent,
  ],
  templateUrl: './workflow-configuration-details.component.html',
  styleUrl: './workflow-configuration-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '(pointerdown)': 'userInteracted = true',
    '(keydown)': 'userInteracted = true',
  },
})
export class WorkflowConfigurationDetailsComponent
  implements OnInit, CanDeactivateComponent, OnDestroy
{
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  embeddedWorkflowId = input<string | null>(null);
  embeddedStepId = input<number | null>(null);
  embeddedSaved = output<void>();
  isEmbedded = computed(() => !!this.embeddedWorkflowId());

  transloco = inject(TranslocoService);
  workflowService = inject(WorkflowService);
  activatedRoute = inject(ActivatedRoute);
  modalService = inject(ModalServiceService);
  toaster = inject(ToasterService);
  formService = inject(FormService);
  router = inject(Router);
  tenantService = inject(TenantService);
  featureFlagsService = inject(FeatureFlagsService);

  discard = viewChild<TemplateRef<unknown>>('discard');

  workflowId = signal<string>('');
  workflowDetails = signal<RequestDetailsResponse>({
    data: requestDetailsEmptyData,
  });
  showID = signal<boolean>(false);
  showDescription = signal<boolean>(true);
  showCategory = signal<boolean>(true);
  workFlowStepsList = signal<WorkFlowStep[]>([]);
  isWorkflowDetailsLoading = signal<boolean>(false);
  isWorkflowStepsLoading = signal<boolean>(false);
  subscription = new Subscription();

  stepInfoForm = signal<FormGroup>(new FormGroup({}));
  initialFormValues = signal<any>({});
  isFormChanged = signal<boolean>(false);
  // Embedded only: the step form's ngOnInit and the selects' init emits rewrite values after the
  // baseline is taken, so the baseline follows the form until the user first touches it.
  userInteracted = false;
  assigneeSelectDependency = signal<AssigneeInputDependency[]>([]);
  resourceSelectDependency = signal<AssigneeInputDependency>(
    assigneeSelectDependencyEmpty
  );
  disableExpand = signal<boolean>(false);
  isUpdateStepLoading = signal<boolean>(false);
  selectedStep = signal<WorkFlowStep | null>(null);
  assigneeReferenceArray = signal<any[]>([]);
  destroy$ = new Subject<void>();
  requestActionsEnum = RequestActionEnum;

  canBeSentBackToFirstPosition = computed(() => {
    return this.workflowDetails().data.can_be_sent_back_to_first_position;
  });

  get assigneeForm(): FormArray {
    return this.stepInfoForm().get('assignees') as FormArray;
  }

  get escalationForm(): FormGroup {
    return this.stepInfoForm().get('escalation') as FormGroup;
  }

  canDeactivate(): boolean {
    if (this.workFlowStepsList().some(step => step.mode === 'edit')) {
      this.openDiscardModal();
      return false;
    }
    return true;
  }

  ngOnInit() {
    const embeddedId = this.embeddedWorkflowId();
    if (embeddedId) {
      this.workflowId.set(embeddedId);
      this.getWorkFlowShow(embeddedId);
      this.getWorkFlowStepsList(embeddedId);
      return;
    }
    this.subscription.add(
      this.activatedRoute.params.subscribe(params => {
        this.workflowId.set(params['workflowId']);
        this.getWorkFlowShow(params['workflowId']);
        this.getWorkFlowStepsList(params['workflowId']);
      })
    );
  }

  getWorkFlowShow(flowTemplateId: string) {
    this.isWorkflowDetailsLoading.set(true);
    this.workflowService.getWorkFlowShow(flowTemplateId).subscribe({
      next: (response: RequestDetailsResponse) => {
        console.log(response);
        this.workflowDetails.set(response);
        this.isWorkflowDetailsLoading.set(false);
      },
      error: () => {
        if (!this.isEmbedded()) {
          this.router.navigate([
            this.tenantService.adminPath(
              `${ADMIN_ROUTES.WORKFLOW.CONFIGURATIONS}`
            ),
          ]);
        }
        this.isWorkflowDetailsLoading.set(false);
      },
    });
  }

  getWorkFlowStepsList(flowTemplateId: string) {
    this.isWorkflowStepsLoading.set(true);
    this.workflowService
      .getWorkFlowStepsList(flowTemplateId)
      .subscribe((response: ApiResponse<WorkFlowStep>) => {
        const stepList = response.data.map(step => {
          Object.assign(step, { mode: 'view', isExpanded: false });
          return step;
        });
        const onlyStepId = this.embeddedStepId();
        const visible = onlyStepId ? stepList.filter(step => step.id === onlyStepId) : stepList;
        this.workFlowStepsList.set(visible);
        this.isWorkflowStepsLoading.set(false);
        if (onlyStepId && visible[0]) {
          this.switchStepIntoEditMode(visible[0]);
        }
        return;
      });
  }

  switchStepIntoEditMode(step: WorkFlowStep) {
    this.selectedStep.set(step);
    this.workFlowStepsList().forEach(item => {
      item.isExpanded = item.id === step.id;
      item.mode = item.id === step.id ? 'edit' : 'view';
    });
    this.disableExpand.set(true);
    // reset the form values and validators
    this.stepInfoForm.set(this.createStepInfoForm());
    this.patchFormValues(step);
    this.setSelectInputsDependencies(step);
    this.initialFormValues.set(this.stepInfoForm().getRawValue());
    this.userInteracted = false;
    this.checkForChanges();
    this.setupChangeDetection();
    this.updateResourceDependencyOnValueChange(step);
  }

  private isAddCollaboratorsStepActionEnabled(): boolean {
    return this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOW_CONFIGURATION.WORKFLOW_STEP_ACTIONS
        .add_collaborators
    );
  }

  createStepInfoForm() {
    return new FormGroup({
      assignees: new FormArray([
        new FormGroup({
          assignable_type: new FormControl('', Validators.required),
          assignable_id: new FormControl('', Validators.required),
          preference: new FormControl(null, Validators.required),
          option: new FormControl(null, Validators.required),
          assignment_preference: new FormControl(null, Validators.required),
        }),
      ]),
      sla: new FormControl('', [Validators.required]),
      sla_type: new FormControl('', Validators.required),
      sla_variable: new FormControl(''),
      escalation: new FormGroup({
        type: new FormControl(null),
        escalation_type: new FormControl(null),
        stakeholder_type: new FormControl(null),
        stakeholder_id: new FormControl(null),
        variable: new FormControl(null),
        when: new FormControl(null),
      }),
      assignment_preference_type: new FormControl('and'),
      completion_action: new FormControl('', [Validators.required]),
      actions: new FormGroup({
        send_back: new FormControl(false),
        send_back_steps: new FormControl(null),
        reject: new FormControl(false),
        send_forward: new FormControl(false),
        re_assign: new FormControl(false),
      }),
      ...(this.isAddCollaboratorsStepActionEnabled()
        ? { step_actions: createStepActionsFormArray() }
        : {}),
    });
  }

  patchFormValues(step: WorkFlowStep) {
    this.assigneeForm.clear();
    const assignees = step.assignees ?? [];
    this.assigneeReferenceArray.set([]);
    assignees.forEach((assignee, index) => {
      if (assignee.type == 'static') {
        this.assigneeReferenceArray().push({
          id: index,
          assignable_type: assignee.assignable_type,
          assignable_id: assignee.data.id,
          preference: assignee.preference,
          assignment_preference: assignee.preference
            ? 'at_least'
            : 'all_assignees',
        });
        const newGroup = new FormGroup({
          assignable_type: new FormControl(
            assignee.assignable_type,
            Validators.required
          ),
          assignable_id: new FormControl(assignee.data.id, Validators.required),
          option: new FormControl(assignee.option?.value, Validators.required),
          preference: new FormControl(
            assignee.preference,
            assignee.preference ? Validators.required : null
          ),
          assignment_preference: new FormControl(
            assignee.preference ? 'at_least' : 'all_assignees',
            Validators.required
          ),
        });
        this.assigneeForm.push(newGroup);
      } else {
        this.assigneeReferenceArray().push({
          id: index,
          assignable_type: assignee.type,
          assignable_id: assignee.variable,
          preference: assignee.preference,
          assignment_preference: assignee.preference
            ? 'at_least'
            : 'all_assignees',
        });
        const newGroup = new FormGroup({
          assignable_type: new FormControl(assignee.type, Validators.required),
          assignable_id: new FormControl(
            assignee.data?.id,
            Validators.required
          ),
          option: new FormControl(assignee.option?.value, Validators.required),
          preference: new FormControl(
            assignee.preference,
            assignee.preference ? Validators.required : null
          ),
          assignment_preference: new FormControl(
            assignee.preference ? 'at_least' : 'all_assignees',
            Validators.required
          ),
        });
        this.assigneeForm.push(newGroup);
      }
    });
    this.stepInfoForm().patchValue({
      sla: step.sla.type === 'static' ? step.sla.value : null,
      sla_type: step.sla.type,
      sla_variable:
        step.sla.type === 'variable' ? step.sla.variable.value : null,
      assignment_preference_type: 'and',
      completion_action: step.completion_action ?? '',
      escalation: {
        type: step.escalation?.type?.value ?? null,
        escalation_type: step.escalation?.escalation_type?.value ?? null,
        stakeholder_type:
          (step.escalation?.stakeholder_type || step.escalation?.type?.value) ??
          null,
        variable: step.escalation?.stakeholder_data?.id ?? null,
        stakeholder_id: step.escalation?.stakeholder_data.id ?? null,
        when: step.escalation?.stakeholder_data.id ? 'at_deadline' : null,
      },
      actions: {
        send_back: this.getRequestActionValue(
          RequestActionEnum.SENDBACK,
          step.actions
        ),
        send_back_steps: this.getRequestActionValue(
          RequestActionEnum.SENDBACK,
          step.actions
        ).toString(),
        reject: this.getRequestActionValue(
          RequestActionEnum.REJECT,
          step.actions
        ),
        send_forward: this.getRequestActionValue(
          RequestActionEnum.SENDFORWARD,
          step.actions
        ),
        re_assign: this.getRequestActionValue(
          RequestActionEnum.REASSIGN,
          step.actions
        ),
      },
    });
    this.patchStepActions(step);
  }

  private patchStepActions(step: WorkFlowStep) {
    const stepActionsForm = this.stepInfoForm().get(
      'step_actions'
    ) as FormArray | null;

    if (!stepActionsForm) {
      return;
    }

    stepActionsForm.clear();
    const configuredActions = step.step_actions ?? [];

    configuredActions.forEach(action => {
      stepActionsForm.push(createStepActionFormGroupFromConfig(action));
    });
  }

  getRequestActionValue(action: string, actions: StepAction[]) {
    if (actions) return actions.find(a => a.value === action)?.preference ?? 0;

    return 0;
  }

  setSelectInputsDependencies(step: WorkFlowStep) {
    const dependencies = step.assignees.map(assignee =>
      this.getAssigneeType(assignee)
    );
    this.assigneeSelectDependency.set(dependencies);
    if (step.escalation) {
      const stakeholder = {
        type: step.escalation.stakeholder_type,
        data: step.escalation.stakeholder_data,
      };
      this.resourceSelectDependency.set(this.getAssigneeType(stakeholder));
    }
    this.setEscalationStakeholderValidationBasedOnTypeValue();
    this.assigneeForm.controls.forEach((_, index) => {
      this.updateAssigneeDependencyOnInit(index, step.assignees[index].data);
      this.updateAssigneeDependencyOnValueChange(index);
    });
  }

  setEscalationStakeholderValidationBasedOnTypeValue() {
    const whenControl = this.escalationForm.get('when') as FormControl;
    const stakeholderTypeControl = this.escalationForm.get(
      'stakeholder_type'
    ) as FormControl;
    const stakeholderIdControl = this.escalationForm.get(
      'stakeholder_id'
    ) as FormControl;
    const escalationTypeControl = this.escalationForm.get(
      'escalation_type'
    ) as FormControl;
    const variableControl = this.escalationForm.get('variable') as FormControl;

    this.stepInfoForm()
      .get('escalation')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const hasData = Object.values(value).some(value => value);

        if (hasData) {
          if (stakeholderTypeControl.value === EscalationTypeEnum.VARIABLE) {
            variableControl.setValidators([Validators.required]);
            stakeholderIdControl.clearValidators();
          } else {
            variableControl.clearValidators();
            stakeholderIdControl.setValidators([Validators.required]);
          }
          stakeholderTypeControl.setValidators([Validators.required]);
          whenControl.setValidators([Validators.required]);
          escalationTypeControl.setValidators([Validators.required]);
        } else {
          stakeholderTypeControl.clearValidators();
          stakeholderIdControl.clearValidators();
          whenControl.clearValidators();
          escalationTypeControl.clearValidators();
          variableControl.clearValidators();
        }
        stakeholderTypeControl.updateValueAndValidity({ emitEvent: false });
        stakeholderIdControl.updateValueAndValidity({ emitEvent: false });
        whenControl.updateValueAndValidity({ emitEvent: false });
        escalationTypeControl.updateValueAndValidity({ emitEvent: false });
        variableControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  updateResourceDependencyOnValueChange(step: WorkFlowStep) {
    const resourceTypeControl = this.escalationForm.get(
      'stakeholder_type'
    ) as FormControl;
    const resourceIdControl = this.escalationForm.get(
      'stakeholder_id'
    ) as FormControl;
    resourceTypeControl.valueChanges.subscribe(value => {
      resourceIdControl.reset();
      const stakeholder = {
        type: value,
        data: step.escalation?.stakeholder_data,
      };
      this.setEscalationStakeholderValidationBasedOnTypeValue();
      const assigneeType = this.getAssigneeType(stakeholder);
      this.resourceSelectDependency.set(assigneeType);
    });
  }

  updateAssigneeDependencyOnInit(
    index: number,
    data: AssigneeData | undefined
  ) {
    // Early return if assigneeForm is null or undefined
    if (!this.assigneeForm) {
      // console.warn('Assignee form is not available');
      return;
    }

    // Early return if index is out of bounds
    if (index < 0 || index >= this.assigneeForm.controls.length) {
      // console.warn(
      //   `Index ${index} is out of bounds for assignee form controls`
      // );
      return;
    }

    const assignableTypeControl = this.assigneeForm.controls[index]?.get(
      'assignable_type'
    ) as FormControl;

    // Early return if control is not available
    if (!assignableTypeControl) {
      // console.warn(`Assignable type control not found at index ${index}`);
      return;
    }

    const value = assignableTypeControl.value;
    this.removeValidationsForUserType(value, index);

    let assigneeData;
    if (typeof data !== 'string') {
      assigneeData = {
        id: data?.id ?? '',
        name: data?.name ?? '',
      };
    }

    const assignee = {
      type: value,
      data: assigneeData,
    };
    const assigneeType = this.getAssigneeType(assignee);
    this.assigneeSelectDependency()[index] = assigneeType;
  }

  updateAssigneeDependencyOnValueChange(index: number) {
    // Early return if assigneeForm is null or undefined
    if (!this.assigneeForm) {
      // console.warn('Assignee form is not available');
      return;
    }

    // Early return if index is out of bounds
    if (index < 0 || index >= this.assigneeForm.controls.length) {
      // console.warn(
      //   `Index ${index} is out of bounds for assignee form controls`
      // );
      return;
    }

    const assignableTypeControl =
      this.assigneeForm.controls[index]?.get('assignable_type');

    // Early return if control is not available
    if (!assignableTypeControl) {
      // console.warn(`Assignable type control not found at index ${index}`);
      return;
    }

    assignableTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.removeValidationsForUserType(value, index);
        const assignee = {
          type: value,
          data: { id: '', name: '' },
        };
        const assigneeType = this.getAssigneeType(assignee);
        this.assigneeSelectDependency()[index] = assigneeType;
      });
  }

  // this function removes the validations for assignee preference if user type is selected
  removeValidationsForUserType(type: string, index: number) {
    if (type === 'user') {
      const formGroup = this.assigneeForm.controls[index] as FormGroup;

      // Early return if form group is not available
      if (!formGroup) {
        // console.warn(`Form group not found at index ${index}`);
        return;
      }

      const preferenceController = formGroup.controls['preference'];
      const assignmentPreferenceController =
        formGroup.controls['assignment_preference'];

      // Early return if controllers are not available
      if (!preferenceController || !assignmentPreferenceController) {
        // console.warn(`Required form controls not found at index ${index}`);
        return;
      }

      // Force remove validation for user type
      preferenceController.setValue(null);
      preferenceController.clearValidators();
      preferenceController.setErrors(null);
      preferenceController.updateValueAndValidity({
        onlySelf: false,
        emitEvent: true,
      });

      assignmentPreferenceController.setValue(null);
      assignmentPreferenceController.clearValidators();
      assignmentPreferenceController.setErrors(null);
      assignmentPreferenceController.updateValueAndValidity({
        onlySelf: false,
        emitEvent: true,
      });
    }
  }

  setupChangeDetection() {
    this.stepInfoForm().valueChanges.subscribe(() => {
      this.checkForChanges();
    });
  }

  checkForChanges() {
    const formRawValues = this.stepInfoForm().getRawValue();
    const currentValues = formRawValues;
    if (this.isEmbedded() && !this.userInteracted) {
      this.initialFormValues.set(formRawValues);
    }
    const hasChanges =
      JSON.stringify(currentValues) !==
      JSON.stringify(this.initialFormValues());
    this.isFormChanged.set(hasChanges);
  }

  getAssigneeType(assignee: any): AssigneeInputDependency {
    const baseApiUrl = `${baseUrl}/lookup`;
    const defaultFilters = [{ key: 'search', value: '' }];
    const activeStateFilter = [{ key: 'state', value: 'active' }];
    const notInactiveStateFilter = [
      { key: 'not_states][0', value: 'inactive' },
    ];
    const notCancelledStateFilter = [
      { key: 'not_states][1', value: 'cancelled' },
    ];
    // the variable option is returned as a data object from the backend
    const defaultSelectedOption = {
      label: assignee.data?.name ?? '',
      value: assignee.data?.id ?? '',
    };

    switch (assignee.type) {
      case 'department':
        return {
          api: `${baseApiUrl}/departments`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_configurations_details_screen_select_department_placeholder'
          ),
          filters: [...defaultFilters],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_department_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      case 'group':
        return {
          api: `${baseApiUrl}/groups`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_configurations_details_screen_select_group_placeholder'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_group_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      case 'legal_entity':
        return {
          api: `${baseApiUrl}/legal-entities`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_step_info_form_{legal_entity}_label'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_legal_entity_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      case 'variable':
        return {
          api: `${baseApiUrl}/flow-templates/${this.workflowId()}/assignee-variables`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_step_info_form_variable option_label'
          ),
          filters: [],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_legal_entity_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      case 'project_role':
        return {
          api: `${baseApiUrl}/project-roles`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_step_info_form_select_project_role_label'
          ),
          filters: [],
          searchLabel: '',
          searchPlaceholder: '',
        };
      case 'fields':
        return {
          api: `${baseApiUrl}/flow-templates/${this.workflowId()}/assignee-fields`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_step_info_form_select_{fields}_label'
          ),
          filters: [],
          searchLabel: '',
          searchPlaceholder: '',
        };
      case 'position':
        return {
          api: `${baseApiUrl}/positions`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_step_info_form_select_{position}_label'
          ),
          filters: [...defaultFilters],
          searchLabel: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
      default:
        return {
          api: `${baseApiUrl}/users`,
          selectedOption: defaultSelectedOption,
          placeholder: this.transloco.translate(
            'workflow_configurations_details_screen_select_user_placeholder'
          ),
          filters: [
            ...defaultFilters,
            ...notInactiveStateFilter,
            ...notCancelledStateFilter,
          ],
          searchLabel: this.transloco.translate(
            'workflow_configurations_details_screen_user_search_label'
          ),
          searchPlaceholder: this.transloco.translate(
            'user_dropdown_input_search_placeholder'
          ),
        };
    }
  }

  openDiscardModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      title: '',
      customizeContent: this.discard(),
    };
    this.modalService.modalContent.set(modal);
  }

  closeDiscardModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  submitForm(step: WorkFlowStep | null) {
    const formRawValues = this.stepInfoForm().value;
    const modifiedFormValues: any = {
      assignees: formRawValues.assignees,
      completion_action: formRawValues.completion_action,
      escalation: formRawValues.escalation
        ? Object.values(formRawValues.escalation).every(value => value === null)
          ? null
          : formRawValues.escalation
        : null,
      sla_type: formRawValues.sla_type,
      actions: [
        {
          value: RequestActionEnum.REASSIGN,
          preference: formRawValues.actions.re_assign ? 1 : 0,
        },
        {
          value: RequestActionEnum.SENDBACK,
          preference: formRawValues.actions.send_back_steps
            ? +formRawValues.actions.send_back_steps
            : 0,
        },
        {
          value: RequestActionEnum.SENDFORWARD,
          preference: formRawValues.actions.send_forward ? 1 : 0,
        },
        {
          value: RequestActionEnum.REJECT,
          preference: formRawValues.actions.reject ? 1 : 0,
        },
      ],
    };

    if (this.isAddCollaboratorsStepActionEnabled()) {
      modifiedFormValues.step_actions = formRawValues.step_actions;
    }

    // Add sla or sla_variable based on sla_type
    if (formRawValues.sla_type === 'static') {
      modifiedFormValues.sla = formRawValues.sla;
    } else if (formRawValues.sla_type === 'variable') {
      modifiedFormValues.sla_variable = formRawValues.sla_variable;
    }

    if (
      formRawValues.escalation &&
      formRawValues.escalation.stakeholder_type === EscalationTypeEnum.VARIABLE
    ) {
      modifiedFormValues.escalation.type = EscalationTypeEnum.VARIABLE;
      delete modifiedFormValues.escalation.stakeholder_id;
      delete modifiedFormValues.escalation.stakeholder_type;
    } else if (
      formRawValues.escalation &&
      formRawValues.escalation.stakeholder_type !== null &&
      formRawValues.escalation.stakeholder_type !== EscalationTypeEnum.VARIABLE
    ) {
      modifiedFormValues.escalation.type = EscalationTypeEnum.STATIC;
      delete modifiedFormValues.escalation.variable;
    }

    modifiedFormValues.assignees.forEach((assignee: any, index: number) => {
      if (assignee.assignable_type === 'variable') {
        modifiedFormValues.assignees[index] = {
          variable: assignee.assignable_id,
          type: assignee.assignable_type,
        };
      } else if (assignee.assignable_type === 'project_role') {
        modifiedFormValues.assignees[index] = {
          role_id: assignee.assignable_id,
          type: assignee.assignable_type,
        };
      } else if (assignee.assignable_type === 'fields') {
        modifiedFormValues.assignees[index] = {
          field_id: assignee.assignable_id,
          type: assignee.assignable_type,
        };
      } else if (
        assignee.assignable_type &&
        assignee.assignable_type !== 'variable' &&
        assignee.assignable_type !== 'project_role' &&
        assignee.assignable_type !== 'fields'
      ) {
        assignee.type = 'static';
      }
    });

    if (this.stepInfoForm().invalid) {
      this.stepInfoForm().markAllAsTouched();
    }
    if (this.stepInfoForm().valid && step) {
      this.isUpdateStepLoading.set(true);
      if (
        !this.stepInfoForm().value.escalation ||
        !this.stepInfoForm().value.escalation.escalation_type
      ) {
        delete this.stepInfoForm().value.escalation;
      }

      this.workflowService
        .updateWorkFlowStep(this.workflowId(), step.id, modifiedFormValues)
        .subscribe({
          next: () => this.handleUpdateStepSuccessResponse(),
          error: (error: any) => this.handleUpdateStepErrorResponse(error),
        });
    }
    this.subscription.unsubscribe();
  }

  handleUpdateStepSuccessResponse() {
    this.discardForm();
    this.getWorkFlowStepsList(this.workflowId());
    this.isUpdateStepLoading.set(false);
    if (this.isEmbedded()) this.embeddedSaved.emit();
  }

  handleUpdateStepErrorResponse(error: any) {
    this.formService.handleFormArrayErrors(error, this.stepInfoForm());
    this.isUpdateStepLoading.set(false);
    this.toaster.add(error.message, '', '', 'error', true, true);
  }

  discardForm() {
    const stepList = this.workFlowStepsList().map(step => {
      Object.assign(step, { mode: 'view', isExpanded: false });
      return step;
    });
    this.disableExpand.set(false);
    this.workFlowStepsList.set(stepList);
    this.selectedStep.set(null);
    this.closeDiscardModal();
    this.subscription.unsubscribe();
  }

  addNewFormGroup(formGroup: FormGroup) {
    this.assigneeForm.push(formGroup);
    const defaultDependency: AssigneeInputDependency = {
      api: '',
      selectedOption: { label: '', value: '' },
      placeholder: '',
      filters: [],
      searchLabel: '',
      searchPlaceholder: '',
    };
    this.assigneeSelectDependency().push(defaultDependency);
    this.updateAssigneeDependencyOnValueChange(this.assigneeForm.length - 1);
    this.assigneeReferenceArray().push({
      ...formGroup.value,
      id: this.assigneeReferenceArray().length,
    });
  }

  deleteFormGroup(index: number) {
    this.assigneeForm.removeAt(index);
    this.assigneeReferenceArray().splice(index, 1);
    this.assigneeSelectDependency().splice(index, 1);
    this.assigneeSelectDependency().forEach((_, i) => {
      this.updateAssigneeDependencyOnValueChange(i);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
